import { z } from 'zod'

import {
  OptionInputFieldResponseFieldTypeEnum,
  OptionResponse,
} from '../apis/fvillage/api'
import i18n from '../i18n'

const regex = /^[0-9]+$/

export const optionItemSchema = z.object({
  items: z.array(
    z.object({
      option: z.string(), // Option id
      count: z.string().regex(regex, i18n.t('validations.number')).nullish(),
      // Absent on items stored before options had input fields.
      answers: z
        .array(
          z.object({
            inputField: z.number(),
            value: z
              .string()
              .max(255, i18n.t('validations.maxlength', { maximum: 255 })),
          })
        )
        .optional(),
    })
  ),
})

export type OptionItem = z.infer<typeof optionItemSchema>

// Mirrors the backend answer validation (SB-2583): answers are checked only
// for options with a count, a required answer must be non-blank after trim,
// and a non-blank number answer must be ASCII digits only.
export const createOptionItemSchema = (
  options: Pick<OptionResponse, 'id' | 'inputFields'>[]
) =>
  optionItemSchema.superRefine(({ items }, context) => {
    items.forEach((item, itemIndex) => {
      if (!Number(item.count)) {
        return
      }
      const inputFields =
        options.find((option) => option.id?.toString() === item.option)
          ?.inputFields ?? []
      inputFields.forEach((inputField, fieldIndex) => {
        const answerIndex =
          item.answers?.findIndex(
            (answer) => answer.inputField === inputField.id
          ) ?? -1
        const value = item.answers?.[answerIndex]?.value.trim() ?? ''
        const path = [
          'items',
          itemIndex,
          'answers',
          answerIndex === -1 ? fieldIndex : answerIndex,
          'value',
        ]
        if (inputField.isRequired && value === '') {
          context.addIssue({
            code: z.ZodIssueCode.custom,
            message: i18n.t('validations.required'),
            path,
          })
        }
        if (
          inputField.fieldType ===
            OptionInputFieldResponseFieldTypeEnum.Number &&
          value !== '' &&
          !regex.test(value)
        ) {
          context.addIssue({
            code: z.ZodIssueCode.custom,
            message: i18n.t('validations.nonNegativeInteger'),
            path,
          })
        }
      })
    })
  })
